import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'

import Field from '../../../../components/Form/Field'
import EditForm from '../../../../features/components/Forms/EditForm'

import { usePurifiedFormik } from '../../../../hooks/formHooks/usePurifiedFormik'
import { formatNumber } from '../../../../helpers/numbers'

import { clearUpdateCampaign, updateCampaign } from '../../../../modules/actions/campaigns'
import { selectedDV360AdAccountSelector } from '../../../../modules/selectors/adAccounts'

import {
  campaignUpdateErrorSelector,
  campaignUpdateIsLoadingSelector,
  campaignWasUpdatedSelector
} from '../../../../modules/selectors/campaigns'

import { BUDGET_LIFETIME } from '../../../Multiplatform/CampaignForms/fields'
import { DV_360_PLATFORM } from '../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../constants/changeHistory'
import { getBudgetValidation } from '../remainingBudgetValidation'
import { START_DATE } from '../../../ReusableFormFields/StartTimeFields/fields'
import { STOP_DATE } from '../../../ReusableFormFields/StopTimeFields/fields'

const BudgetSectionForm = ({ editItemData: campaign, remainingBudget, updateOptions, title, ...formProps }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { ad_account_id: adAccountId } = campaign
  const { currency_symbol: currencySymbol } = useSelector(selectedDV360AdAccountSelector)

  const successUpdate = useSelector(campaignWasUpdatedSelector)

  const handleSubmit = useCallback(
    values => {
      const updateCampaignData = {
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        },
        account: adAccountId,
        [BUDGET_LIFETIME]: values[BUDGET_LIFETIME],
        // When we update budget - we also need to include start and stop dates (dv360 requires it)
        [START_DATE]: campaign[START_DATE],
        [STOP_DATE]: campaign[STOP_DATE]
      }

      dispatch(updateCampaign(updateCampaignData, campaign.id, DV_360_PLATFORM, updateOptions))
    },
    [dispatch, title, adAccountId, updateOptions, campaign]
  )

  const initialCampaignBudgetLifetime = useMemo(() => campaign?.budget_lifetime || '', [campaign])

  const initialValues = useMemo(
    () => ({
      [BUDGET_LIFETIME]: initialCampaignBudgetLifetime
    }),
    [initialCampaignBudgetLifetime]
  )

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: getBudgetValidation({
      remainingBudget,
      initialBudget: initialCampaignBudgetLifetime,
      budgetType: BUDGET_LIFETIME
    })
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const handleClearUpdateCampaign = useCallback(() => {
    dispatch(clearUpdateCampaign())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={successUpdate}
      clearEditItem={handleClearUpdateCampaign}
      errorSelector={campaignUpdateErrorSelector}
      isLoadingSelector={campaignUpdateIsLoadingSelector}
      {...formProps}
    >
      <p>{t('A total budget for the whole campaign.')}</p>
      {remainingBudget && (
        <p>{t(`Your remaining budget is ${currencySymbol}` + formatNumber(remainingBudget, { max: 2 }))}</p>
      )}
      <Field
        type="number"
        formik={purifiedFormik}
        name={BUDGET_LIFETIME}
        placeholder={t('Lifetime Budget')}
        symbol={currencySymbol}
        autoComplete="off"
      />
    </EditForm>
  )
}

export default BudgetSectionForm
